import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { X, Download, Plus, Upload } from 'lucide-react';
import { useTheme } from '../lib/theme';
import {
  listReceipts, getStats, getCategorizationStats,
  bulkCategorize, bulkApprove, bulkDelete, bulkMarkDuplicate,
  bulkClassifyAsset, bulkTaxCode, bulkTaxCodeV2,
} from '../lib/api';
import ManualEntryModal from '../components/ManualEntryModal';
import ReceiptFilters, { SORTS } from '../components/receipts/ReceiptFilters';
import ReceiptTable from '../components/receipts/ReceiptTable';
import BulkActionsBar from '../components/receipts/BulkActionsBar';
import BulkPublishModal from '../components/quickbooks/BulkPublishModal';
import ConfirmDialog from '../components/ConfirmDialog';
import UploadReceiptsModal from '../components/receipts/UploadReceiptsModal';
import UploadsDock from '../components/receipts/UploadsDock';

export default function Receipts() {
  const { colors, effective } = useTheme();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState(searchParams.get('status') || 'pending');
  const [categorization, setCategorization] = useState(searchParams.get('categorization') || 'all');
  const [sort, setSort] = useState('confidence_asc');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [showManualModal, setShowManualModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMinimized, setUploadMinimized] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [sortOpen, setSortOpen] = useState(false);

  // Bulk selection — three states:
  //   `selectMode=false`               → normal browsing
  //   `selectMode=true, all-in-view`   → operates over the full filter set
  //   `selectMode=true, ids selected`  → operates over explicit ids
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [allInView, setAllInView] = useState(false);
  const inSelect = selectMode;
  const selectionCount = allInView ? total : selected.size;

  // Phase 25.3 — Bulk publish modal
  const [bulkPublishOpen, setBulkPublishOpen] = useState(false);
  const bulkPublishIds = allInView
    ? items.map((r) => r.id)
    : Array.from(selected);

  // Toast
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg, duration = 3000) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), duration);
  }, []);

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(h);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lst, st, cs] = await Promise.all([
        listReceipts({ status, categorization, sort, limit: 200, ...(q ? { q } : {}) }),
        getStats(),
        getCategorizationStats(),
      ]);
      setItems(lst.items);
      setTotal(lst.total);
      setPendingCount(st.pending);
      setNeedsReviewCount(cs.needs_review);
    } catch (err) {
      console.error('Failed to load receipts list:', err);
    }
    setLoading(false);
  }, [status, categorization, sort, q]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = useCallback((id) => {
    setAllInView(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectAllPage = useCallback(() => {
    setAllInView(false);
    setSelected(new Set(items.map((i) => i.id)));
  }, [items]);

  const selectAllInView = useCallback(() => {
    setAllInView(true);
    setSelected(new Set(items.map((i) => i.id))); // still track page ids for row highlight
  }, [items]);

  const clearSelect = useCallback(() => {
    setSelected(new Set());
    setAllInView(false);
    setSelectMode(false);
  }, []);

  // Build the current filter payload — matches the shape of GET /api/receipts.
  const currentFilter = useMemo(() => {
    const f = {};
    if (status && status !== 'all') f.status = status;
    if (categorization && categorization !== 'all') f.categorization = categorization;
    if (q) f.q = q;
    return f;
  }, [status, categorization, q]);

  const selectorOrNull = useCallback(() => {
    if (allInView) return { filter: currentFilter };
    const ids = Array.from(selected);
    if (ids.length === 0) return null;
    return { ids };
  }, [allInView, currentFilter, selected]);

  const runBulk = useCallback(async (fn, verb) => {
    const sel = selectorOrNull();
    if (!sel) return;
    try {
      const r = await fn(sel);
      const count = r?.updated ?? r?.matched ?? 0;
      showToast(`${count} receipt${count !== 1 ? 's' : ''} ${verb}`);
      clearSelect();
      await load();
    } catch (err) {
      console.error('Bulk action failed:', err);
      showToast(`Bulk ${verb} failed`);
    }
  }, [selectorOrNull, showToast, clearSelect, load]);

  const doApprove = useCallback(() => runBulk((sel) => bulkApprove(sel, 'approved'), 'approved'), [runBulk]);
  const doDelete = useCallback(() => setConfirmDelete(true), []);
  const runDeleteConfirmed = useCallback(() => {
    setConfirmDelete(false);
    runBulk((sel) => bulkDelete(sel), 'deleted');
  }, [runBulk]);
  const doMarkDuplicate = useCallback(() => runBulk((sel) => bulkMarkDuplicate(sel, true), 'marked duplicate'), [runBulk]);
  const doClassifyAsset = useCallback(
    (isAsset) => runBulk((sel) => bulkClassifyAsset(sel, isAsset), isAsset ? 'flagged as asset' : 'classified as expense'),
    [runBulk],
  );
  const doCategorize = useCallback(
    (name) => runBulk((sel) => bulkCategorize(sel, name), `→ ${name}`),
    [runBulk],
  );
  const doTaxCode = useCallback(
    (key) => runBulk((sel) => bulkTaxCode(sel, key), key ? `tax code → ${key}` : 'tax override cleared'),
    [runBulk],
  );
  const doTaxCodeV2 = useCallback(
    (tc) => {
      const label = tc?.no_tax_applicable
        ? 'no tax'
        : [tc?.federal_code, tc?.provincial_code].filter(Boolean).join(' + ') || 'tax override cleared';
      return runBulk((sel) => bulkTaxCodeV2(sel, tc), `tax code → ${label}`);
    },
    [runBulk],
  );

  const subheader = useMemo(() => {
    if (loading) return 'Loading…';
    const sortLabel = SORTS.find((s) => s.key === sort)?.label?.toLowerCase() || '';
    if (total === 0) return `No ${status === 'all' ? '' : status + ' '}receipts`;
    return `${total} receipt${total !== 1 ? 's' : ''} · ${pendingCount} pending · sorted by ${sortLabel}`;
  }, [loading, total, pendingCount, sort, status]);

  return (
    <div className="flex flex-col h-full" data-testid="receipts-page">
      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-4" style={{ backgroundColor: colors.bg }}>
        {inSelect ? (
          <div className="flex items-center gap-4" data-testid="select-header">
            <button
              data-testid="select-close"
              onClick={clearSelect}
              className="p-1 rounded-md transition-colors hover:opacity-70"
              style={{ color: colors.text }}
            >
              <X size={20} />
            </button>
            <span className="text-lg font-semibold" style={{ color: colors.text }}>
              {selectionCount} selected{allInView ? ' (all in view)' : ''}
            </span>
            <div className="ml-auto flex items-center gap-3">
              <button
                data-testid="select-all-btn"
                onClick={selectAllPage}
                className="text-sm font-semibold transition-colors hover:opacity-70"
                style={{ color: colors.accent }}
              >
                Select page ({items.length})
              </button>
              <button
                data-testid="select-all-in-view-btn"
                onClick={selectAllInView}
                className="text-sm font-semibold transition-colors hover:opacity-70"
                style={{ color: colors.accent }}
              >
                Select all in view ({total})
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-2xl font-bold tracking-tight" style={{ color: colors.text }}>
                Receipts
              </h1>
              <div className="flex gap-2">
                <button
                  data-testid="enter-select-btn"
                  onClick={() => { setSelectMode(true); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                >
                  Select
                </button>
                <button
                  data-testid="add-manual-btn"
                  onClick={() => setShowManualModal(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.accent, borderColor: colors.accent, color: '#FFFFFF' }}
                >
                  <Plus size={16} />
                  Add manually
                </button>
                <button
                  data-testid="upload-btn"
                  onClick={() => { setShowUploadModal(true); setUploadMinimized(false); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.accent, borderColor: colors.accent, color: '#FFFFFF' }}
                  aria-label="Upload receipts"
                >
                  <Upload size={16} />
                  Upload
                </button>
                <Link
                  to="/app/export"
                  data-testid="export-btn"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors hover:opacity-80"
                  style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                >
                  <Download size={16} />
                  Export
                </Link>
              </div>
            </div>
            <p className="text-sm" style={{ color: colors.textMuted }} data-testid="receipts-subheader">
              {subheader}
            </p>
          </>
        )}
      </div>

      {/* Filters (hidden in select mode to avoid changing the active filter mid-select) */}
      {!inSelect && (
        <ReceiptFilters
          query={query} setQuery={setQuery}
          status={status} setStatus={setStatus}
          categorization={categorization} setCategorization={setCategorization}
          needsReviewCount={needsReviewCount}
          sort={sort} setSort={setSort}
          sortOpen={sortOpen} setSortOpen={setSortOpen}
          colors={colors}
        />
      )}

      {/* List */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 pb-4">
        <ReceiptTable
          items={items} loading={loading}
          selected={allInView ? new Set(items.map((i) => i.id)) : selected}
          toggleSelect={toggleSelect}
          inSelect={inSelect}
          colors={colors} effective={effective}
        />
      </div>

      {/* Bulk action bar */}
      {inSelect && selectionCount > 0 && (
        <BulkActionsBar
          onCategorize={doCategorize}
          onApprove={doApprove}
          onDelete={doDelete}
          onMarkDuplicate={doMarkDuplicate}
          onClassifyAsset={doClassifyAsset}
          onTaxCode={doTaxCode}
          onTaxCodeV2={doTaxCodeV2}
          onPublishToQb={() => setBulkPublishOpen(true)}
          context="receipts"
          countLabel={`${selectionCount} receipt${selectionCount === 1 ? '' : 's'}`}
          colors={colors}
        />
      )}

      {/* Phase 25.3 — Bulk publish modal */}
      <BulkPublishModal
        open={bulkPublishOpen}
        onClose={() => setBulkPublishOpen(false)}
        receiptIds={bulkPublishIds}
        onDone={() => { load(); }}
      />

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-8 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full text-sm font-semibold text-white z-50 shadow-lg"
          style={{ backgroundColor: colors.text }}
          data-testid="receipts-toast"
        >
          {toast}
        </div>
      )}
      {showManualModal ? (
        <ManualEntryModal
          onClose={() => setShowManualModal(false)}
          onSaved={() => { setShowManualModal(false); load(); }}
        />
      ) : null}

      {showUploadModal && !uploadMinimized ? (
        <UploadReceiptsModal
          onClose={() => { setShowUploadModal(false); setUploadMinimized(false); }}
          onMinimize={() => setUploadMinimized(true)}
          onReceiptCreated={() => load()}
        />
      ) : null}
      <UploadsDock
        visible={(uploadMinimized || !showUploadModal)}
        onOpen={() => { setShowUploadModal(true); setUploadMinimized(false); }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${selectionCount} receipt${selectionCount === 1 ? '' : 's'}?`}
        body="This can't be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={runDeleteConfirmed}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
